import { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import type { Purchase, UnifiedOrder } from './types';
import { fetchPriceWatchQuote, type PriceWatchQuote } from './actions';

const usd = (v: number | string | null | undefined) =>
  v == null ? '—' : `$${Number(v).toFixed(2)}`;

function ago(iso: string | null): string {
  if (!iso) return 'sin chequeo todavía';
  const h = Math.round((Date.now() - new Date(iso).getTime()) / 3600000);
  return h < 1 ? 'hace menos de 1 h' : `hace ${h} h`;
}

/**
 * Botón "Seguir precio/stock" de una compra Amazon en espera (spec 2026-09-20).
 *  - Sin seguimiento: color por defecto; al clic abre el mini formulario (precio objetivo).
 *  - Con seguimiento: verde "Siguiendo $X" + cruz para dejar de seguir.
 */
export function PriceWatchButton({
  purchase,
  order,
  onAction,
}: {
  purchase: Purchase;
  order: UnifiedOrder;
  onAction: (action: string, order: UnifiedOrder, data?: any) => void;
}) {
  const w = purchase.price_watch;
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState<PriceWatchQuote | null>(null);
  const [target, setTarget] = useState<string>('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBusy(true);
    fetchPriceWatchQuote(purchase.id)
      .then((q) => {
        setQuote(q);
        const def = q?.captured_price_usd ?? purchase.unit_cost ?? null;
        setTarget(def != null ? Number(def).toFixed(2) : '');
      })
      .finally(() => setBusy(false));
  }, [open, purchase.id, purchase.unit_cost]);

  if (w) {
    const title = `${w.reason === 'manual' ? 'Manual' : 'Automático'} · ${ago(w.last_check_at)}: ${
      w.last_check_result || 'esperando el aviso de Keepa'
    } · vence ${new Date(w.expires_at).toLocaleDateString('es-AR')}`;
    return (
      <div className="flex items-center gap-1.5">
        <span
          title={title}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-green-600 text-white"
        >
          <Eye size={12} /> Siguiendo {usd(w.target_price_usd)}
          {w.reason !== 'manual' && <span className="opacity-80">· automático</span>}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!confirm('¿Dejar de seguir este producto en Keepa?')) return;
            onAction('price_watch_stop', order, { purchaseId: purchase.id });
          }}
          className="p-0.5 rounded text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-error-light)] transition-colors"
          title="Dejar de seguir"
        >
          <EyeOff size={14} />
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded border border-[var(--color-border)] bg-[var(--color-bg)] hover:bg-[var(--color-bg-hover)] text-[var(--color-text)] transition-colors"
        title="Pedirle a Keepa que avise cuando algún vendedor de Amazon (Amazon mismo o terceros) lo tenga a este precio o menos; el sistema compra solo tras verificar en Amazon precio, entrega y ganancia"
      >
        <Eye size={12} /> Seguir precio/stock
      </button>
    );
  }

  const t = parseFloat(target);
  const max = quote?.max_profit_price_usd ?? null;
  const overMax = max != null && Number.isFinite(t) && t > max;
  return (
    <div
      className="p-2 rounded border border-[var(--color-border-light)] bg-[var(--color-bg)] space-y-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-[var(--color-text-muted)]">Comprar si algún vendedor baja a</span>
        <input
          type="number"
          step="0.01"
          min="0.01"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="w-24 px-1.5 py-0.5 text-xs rounded border border-[var(--color-border)] bg-[var(--color-bg)]"
        />
        {busy && <Loader2 size={12} className="animate-spin" />}
      </div>
      <p className="text-[10px] text-[var(--color-text-muted)]">
        Capturado {usd(quote?.captured_price_usd)} · Amazon hoy {usd(quote?.cached_amazon_price_usd)} ·
        máximo con ganancia mínima {usd(max)}
      </p>
      {overMax && (
        <p className="text-[10px] text-amber-700 dark:text-amber-300">
          Por encima del máximo con ganancia: la compra puede quedar retenida por seguridad.
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          disabled={!Number.isFinite(t) || t <= 0 || busy}
          onClick={() => {
            onAction('price_watch_start', order, { purchaseId: purchase.id, target: t });
            setOpen(false);
          }}
          className="px-2 py-1 text-[11px] font-medium rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 transition-colors"
        >
          Confirmar seguimiento
        </button>
        <button
          onClick={() => setOpen(false)}
          className="px-2 py-1 text-[11px] rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)]"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
